/**
 * The two drag-and-drop wrappers for the week grid: a cell that takes a
 * product chip or an order card, and a card that can be picked up.
 */
import { Box } from "@mui/material";
import React from "react";
import { useDrag, useDrop } from "react-dnd";

import { Product } from "../../../../services/products-api";
import { planner } from "../../planner-ui";
import { ManufacturingOrder } from "../utils/calculations";
import { OrderDragItem, PLAN_DND, ProductDragItem } from "./dnd";

export interface DropCellProps {
	workstation: string;
	day: Date;
	onDropProduct: (product: Product, workstation: string, day: Date) => void;
	onDropOrder: (order: ManufacturingOrder, workstation: string, day: Date) => void;
	sx?: object;
	children: React.ReactNode;
}

export const DropCell: React.FC<DropCellProps> = ({ workstation, day, onDropProduct, onDropOrder, sx, children }) => {
	const [{ isOver, canDrop }, dropRef] = useDrop<ProductDragItem | OrderDragItem, void, { isOver: boolean; canDrop: boolean }>(
		() => ({
			accept: [PLAN_DND.PRODUCT, PLAN_DND.ORDER],
			canDrop: (item) => !("order" in item) || item.order.workstation !== workstation || new Date(item.order.startTime).toDateString() !== day.toDateString(),
			drop: (item) => {
				if ("product" in item) onDropProduct(item.product, workstation, day);
				else onDropOrder(item.order, workstation, day);
			},
			collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }), canDrop: monitor.canDrop() }),
		}),
		[workstation, day, onDropProduct, onDropOrder]
	);
	return (
		<Box
			ref={dropRef}
			sx={{
				...(sx as object),
				...(isOver && canDrop
					? { bgcolor: planner.accentSoft, boxShadow: `inset 0 0 0 2px ${planner.accent}` }
					: isOver
						? { boxShadow: `inset 0 0 0 2px ${planner.hairlineStrong}` }
						: {}),
				transition: "background-color 120ms ease, box-shadow 120ms ease",
			}}
		>
			{children}
		</Box>
	);
};

export const DraggableCard: React.FC<{ order: ManufacturingOrder; children: React.ReactNode }> = ({ order, children }) => {
	const [{ isDragging }, dragRef] = useDrag<OrderDragItem, void, { isDragging: boolean }>(
		() => ({
			type: PLAN_DND.ORDER,
			item: { order },
			collect: (monitor) => ({ isDragging: monitor.isDragging() }),
		}),
		[order]
	);
	return (
		<Box ref={dragRef} sx={{ cursor: "grab", opacity: isDragging ? 0.35 : 1, "&:active": { cursor: "grabbing" } }}>
			{children}
		</Box>
	);
};
